import { createAsyncThunk } from '@reduxjs/toolkit'
import { ColumnFiltersState } from '@tanstack/react-table'
import { Axios } from 'api/axios'
import axios from 'axios'

const API_URL = process.env.REACT_APP_BASE_URL || ''
const baseURL = API_URL.replace(/\/+$/, '') // ensure no trailing slash
const headers = {
  'Content-Type': 'application/json'
}

export interface Organisation {
  id?: number
  packageName: string
  districtCode: string
  districtName: string
  state: string
  status: boolean
  createBy?: string
  updatedBy?: string
  createDate?: string
  updateDate?: string
}
export interface PaginatedResponse<T> {
  count: number
  total_pages: number
  next: string | null
  previous: string | null
  results: T[]
}

/** Helper to build full endpoint URLs */
const buildUrl = (path: string) => `${baseURL}/${path.replace(/^\/+/, '')}`

/**
 * Fetch users with paging/sort/search/filters.
 * Date filters are sent as e.g. created_at_date_from / created_at_date_to
 */
export const fetchUser = createAsyncThunk(
  'user/fetchUsers',
  async (paramsPayload: {
    page: number
    pageSize: number
    search?: string
    sort?: string
    order?: 'asc' | 'desc' | ''
    filters?: ColumnFiltersState
  }) => {
    const { page, pageSize, sort, order, search, filters } = paramsPayload
    try {
      const params: Record<string, any> = {
        page,
        page_size: pageSize
      }

      if (search) params.search = (search || '').trim()

      // Build ordering
      if (sort) {
        const mappedSort = sort === 'user_type_name' ? 'user_type_name' : sort
        params.ordering = order === 'desc' ? `-${mappedSort}` : mappedSort
      }

      // Filters
      if (filters && Array.isArray(filters)) {
        filters.forEach((f: any) => {
          const { id, value } = f
          if (!id || value === undefined || value === '') return

          // Date-range filters: create e.g. created_at_date_from / created_at_date_to
          if ((id === 'updated_at' || id === 'created_at') && value && typeof value === 'object') {
            const dateFilter = value as { from_date?: string; to_date?: string }
            if (dateFilter.from_date) params[`${id}_date_from`] = dateFilter.from_date
            if (dateFilter.to_date) params[`${id}_date_to`] = dateFilter.to_date
            return
          }

          // If value is an object (non-date), flatten as id_key=value (e.g., status_eq=active)
          if (value && typeof value === 'object') {
            Object.entries(value).forEach(([k, v]) => {
              if (v !== undefined && v !== null && v !== '') {
                params[`${id}_${k}`] = v
              }
            })
            return
          }

          // Default scalar filter
          params[id] = value
        })
      }

      const response: any = await Axios.get(('api/user/list/'), { params })
      const responseData = response?.data || {}

      return {
        data: responseData?.data || [],
        rowCount: responseData?.count || 0
      }
    } catch (error: any) {
      console.error('fetchUser error:', error.response?.data || error.message)
      throw new Error('Failed to fetch Users')
    }
  }
)

export const createUser = createAsyncThunk(
  'user/createUser',
  async (userData: any, { rejectWithValue }) => {
    try {
      const response = await Axios.post(buildUrl('api/user/register/'), userData, {
        headers
      })
      return response.data
    } catch (err: any) {
      console.error('Create User Error:', err)
      return rejectWithValue(err.response?.data || 'Failed to create user')
    }
  }
)

export const fetchOrganisationById = createAsyncThunk(
  'organisation/fetchById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await Axios.get(buildUrl(`api/organisation/${id}/`))
      return response
    } catch (err: any) {
      return rejectWithValue(err.response?.data || 'Failed to fetch organisations by ID')
    }
  }
)

export const updateOrganisation = createAsyncThunk(
  'organisation/updateOrganisation',
  async (packageData: any) => {
    const response: any = await Axios.put(
      buildUrl(`api/organization/${packageData.id}/update/`),
      packageData
    )
    return response
  }
)

export const updateStatusInOrg = createAsyncThunk(
  'organisation/updateOrganisation2',
  async (packageData: any) => {
    const response: any = await Axios.put(
      buildUrl(`api/organization/${packageData.id}/update/`),
      packageData
    )
    return response
  }
)

export const addOrganisation = createAsyncThunk(
  'organisation/addOrganisation',
  async (packageData: any) => {
    const response = await Axios.post(buildUrl('api/organisation/'), packageData)
    return response.data
  }
)

export const deleteOrganisation = createAsyncThunk(
  'packages/delete',
  async (id: number, { rejectWithValue }) => {
    try {
      await axios.delete(buildUrl(`api/organization/${id}/`), { headers })
      return id
    } catch (err: any) {
      console.error('Delete error:', err)
      return rejectWithValue(err.response?.data?.message || err.message)
    }
  }
)

export const updateStatusInUsers = createAsyncThunk(
  'updateusers/updateStatusInUsers',
  async (userData: any) => {
    if (!userData?.user_id) {
      throw new Error('user_id is required')
    }
    const response = await Axios.patch(buildUrl(`api/user/edit-status/${userData.user_id}/`), userData)
    return response.data
  }
)

export const fetchDropdownByColumn = createAsyncThunk(
  'organization/fetchDropdownByColumn',
  async (columnId: string) => {
    let endpoint = ''

    switch (columnId) {
      case 'first_name':
        endpoint = 'api/users/dropdown/first_name/'
        break
      case 'last_name':
        endpoint = 'api/users/dropdown/last_name/'
        break
      case 'username':
        endpoint = 'api/users/dropdown/username/'
        break
      case 'organization_name':
        endpoint = 'api/organization/all-org/'
        break
      case 'industry_type':
        endpoint = 'api/organization/dropdown/industry_type/'
        break
      case 'org_city':
        endpoint = 'api/organization/dropdown/org_city/'
        break
      case 'org_state':
        endpoint = 'api/organization/dropdown/org_state/'
        break
      case 'org_pincode':
        endpoint = 'api/organization/dropdown/org_pincode/'
        break
      default:
        throw new Error(`No dropdown endpoint for ${columnId}`)
    }

    const response = await Axios.get(buildUrl(endpoint), { headers })

    // Normalize responses
    if (['first_name', 'last_name', 'username'].includes(columnId)) {
      return {
        key: `${columnId}DropDown`,
        data: Array.isArray(response.data[columnId]) ? response.data[columnId] : []
      }
    }

    if (columnId === 'organization_name') {
      return {
        key: 'orgDropDown',
        data: Array.isArray(response.data.data) ? response.data.data.map((item: any) => item.organization_name) : []
      }
    }

    return {
      key: `${columnId}DropDown`,
      data: Array.isArray(response.data[columnId]) ? response.data[columnId] : []
    }
  }
)

// Export CSV (simple thunk)
export const exportUsersCSV = createAsyncThunk(
  'users/exportUsersCSV',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken')
      const authHeaders = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: '*/*'
      }

      const response = await Axios.get(buildUrl('api/users/list/?export=excel'), {
        headers: authHeaders,
        responseType: 'blob'
      })

      return {
        blob: response.data,
        filename: response.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') || 'users.csv'
      }
    } catch (err: any) {
      console.error('Export CSV error:', err)
      return rejectWithValue(err.message || 'Failed to export CSV')
    }
  }
)

/**
 * Direct export helper — builds same date query params as fetchUser.
 * filters: Array<{ id: string, value: any }>
 */
export const UsersxportsCSV = async ({
  search,
  filters
}: {
  search?: string
  filters?: any
}) => {
  const params = new URLSearchParams()
  params.append('export', 'excel')

  if (search) params.append('search', search)

  if (filters && filters.length > 0) {
    filters.forEach(({ id, value }: { id: string; value: any }) => {
      if (!id || value === undefined || value === null) return

      if ((id === 'created_at' || id === 'updated_at') && value && typeof value === 'object') {
        const dateFilter = value as { from_date?: string; to_date?: string }
        if (dateFilter.from_date) params.append(`${id}_date_from`, dateFilter.from_date)
        if (dateFilter.to_date) params.append(`${id}_date_to`, dateFilter.to_date)
        return
      }

      if (value && typeof value === 'object') {
        // flatten object filters
        Object.entries(value).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== '') {
            params.append(`${id}_${k}`, String(v))
          }
        })
        return
      }

      params.append(id, String(value))
    })
  }

  const response = await Axios.get(buildUrl(`api/users/list/?${params.toString()}`), {
    responseType: 'blob'
  })

  return {
    blob: response.data,
    filename: response.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') || 'Users.csv'
  }
}

export const fetchALLUserFormDropdowns = createAsyncThunk(
  'user/fetchAllUserFormDropdowns',
  async () => {
    try {
      const urls = [
        buildUrl('api/usertype/user-type-dropdown/'),
        buildUrl('api/user/organization-dropdown/')
      ]

      const [userTypesRes, orgRes] = await Promise.all(urls.map(url => Axios.get(url, { headers })))

      return {
        userTypes: Array.isArray(userTypesRes?.data?.data) ? userTypesRes.data.data : [],
        organizations: Array.isArray(orgRes?.data?.data) ? orgRes.data.data : [],
        departments: [],
        teams: [],
        states: []
      }
    } catch (err) {
      console.error('Error fetching user form dropdowns:', err)
      throw err
    }
  }
)
